import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import api from '../services/api';

const ProductDetail = () => {
  const { id } = useParams();
  const { addToCart } = useCart();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [quantity, setQuantity] = useState(1);

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        setLoading(true);
        
        // ESSAYER D'ABORD DE RÉCUPÉRER LE PRODUIT SPÉCIFIQUE
        try {
          const response = await api.get(`/products/${id}`);
          setProduct(response.data);
          setLoading(false);
          return;
        } catch (specificError) {
          console.log('Récupération spécifique échouée, on essaie avec la liste...');
        }

        // SI ÇA ÉCHOUE, RÉCUPÉRER TOUS LES PRODUITS ET FILTRER
        const allProductsResponse = await api.get('/products');
        if (Array.isArray(allProductsResponse.data)) {
          const foundProduct = allProductsResponse.data.find(p => p.id == id);
          if (foundProduct) {
            setProduct(foundProduct);
          } else {
            setError('Produit non trouvé');
          }
        } else {
          setError('Erreur de chargement des données');
        }
      } catch (error) {
        console.error('Erreur chargement produit:', error);
        setError('Erreur lors du chargement du produit');
        setProduct(null);
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
  }, [id]);

  const handleAddToCart = () => {
    if (product) {
      addToCart({ ...product, quantity });
    }
  };

  // Reste du code identique...
  if (loading) {
    return (
      <div className="container-custom py-20 text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
        <p className="mt-4 text-gray-500">Chargement du produit...</p>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="container-custom py-20 text-center">
        <p className="text-gray-500 text-lg">{error || 'Produit non trouvé'}</p>
        <Link to="/products" className="text-primary hover:underline mt-4 inline-block">
          Retour à la boutique
        </Link>
      </div>
    );
  }

  return (
    <div className="container-custom py-6 sm:py-12">
      <nav className="text-sm text-gray-500 mb-4 sm:mb-6">
        <Link to="/" className="hover:text-primary">Accueil</Link>
        <span className="mx-2">/</span>
        <Link to="/products" className="hover:text-primary">Boutique</Link>
        <span className="mx-2">/</span>
        <span className="text-gray-700">{product.name}</span>
      </nav>

      <div className="grid md:grid-cols-2 gap-6 sm:gap-12">
        <div className="bg-gray-100 rounded-2xl h-56 sm:h-72 md:h-96 flex items-center justify-center overflow-hidden">
          {product.image_url ? (
            <img 
              src={product.image_url} 
              alt={product.name} 
              className="w-full h-full object-contain hover:scale-105 transition duration-300"
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = '';
                e.target.className = 'hidden';
                e.target.parentElement.innerHTML = `
                  <div class="w-full h-full flex items-center justify-center">
                    <svg class="w-12 h-12 sm:w-20 sm:h-20 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </div>
                `;
              }}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <svg className="w-12 h-12 sm:w-20 sm:h-20 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
          )}
        </div>

        <div>
          <h1 className="text-2xl sm:text-3xl font-bold mb-2">{product.name}</h1>
          {product.brand && (
            <p className="text-gray-500 text-sm sm:text-base mb-1">{product.brand}</p>
          )}
          {product.format && (
            <p className="text-gray-400 text-sm mb-2">{product.format}</p>
          )}
          
          <div className="flex items-center gap-2 mb-4">
            {product.old_price && (
              <span className="text-gray-400 line-through text-lg sm:text-xl">{product.old_price} €</span>
            )}
            <span className="text-2xl sm:text-3xl text-primary font-bold">{product.price} €</span>
          </div>

          {product.is_premium && (
            <span className="inline-block bg-secondary text-primary text-xs font-semibold px-3 py-1 rounded-full mb-3">
              Premium
            </span>
          )}
          {product.is_best_seller && (
            <span className="inline-block bg-primary text-white text-xs font-semibold px-3 py-1 rounded-full mb-3 ml-2">
              ⭐ Meilleure vente
            </span>
          )}

          <div className="mb-6">
            <h3 className="font-semibold text-sm sm:text-base mb-2">Description</h3>
            <p className="text-sm sm:text-base text-gray-600 leading-relaxed">
              {product.description || 'Aucune description disponible pour ce produit.'}
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
            <div className="flex items-center border rounded-lg">
              <button
                onClick={() => setQuantity(Math.max(1, quantity - 1))}
                className="px-3 sm:px-4 py-2 hover:bg-gray-100 rounded-l-lg"
              >
                -
              </button>
              <span className="px-3 sm:px-4 py-2 min-w-[40px] text-center">{quantity}</span>
              <button
                onClick={() => setQuantity(quantity + 1)}
                className="px-3 sm:px-4 py-2 hover:bg-gray-100 rounded-r-lg"
              >
                +
              </button>
            </div>
            <button 
              onClick={handleAddToCart}
              className="btn-primary w-full sm:w-auto justify-center flex-1"
            >
              Ajouter au panier
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetail;